import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Box, Flex, Text } from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { ClickCatcher } from 'components/common/layout/click-catcher';
import { t } from 'i18next';
import { IMenuAction } from 'interfaces/i-menus';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { RADIX, RadixSide } from 'lib_ts/enums/radix-ui';
import { ReactNode, useMemo, useState } from 'react';

export const SEPARATOR_LABEL = '---SEPARATOR---';

// groups and separates menu actions
export const setupMenuActions = (
  actions: IMenuAction[],
  skipSort?: boolean
) => {
  const groupDict = ArrayHelper.groupBy(
    // prevents groups from being created where no actions would be shown
    actions.filter((a) => !a.invisible),
    'group'
  );

  return Object.keys(groupDict).flatMap((key) => {
    const output: IMenuAction[] = [];

    const group = groupDict[key];

    // insert an entry for the group header (if key is not empty or a separator key starting with _)
    if (key && group && group.length > 0) {
      // print a separator before the group starts
      output.push({
        label: SEPARATOR_LABEL,
        group: key,
        disabled: true,
        onClick: () => {
          // do nothing
        },
      });

      if (!key.startsWith('_')) {
        // this is a regular group name, print it as a disabled option
        output.push({
          label: key,
          group: key,
          disabled: true,
          className: 'MenuGroupLabel',
          onClick: () => {
            // do nothing
          },
        });
      }
    }

    if (!skipSort && group) {
      // sort options within a group alphabetically
      group.sort((a, b) => StringHelper.safeSort(a.label, b.label));
    }

    if (group) {
      output.push(...group);
    }

    return output;
  });
};

interface IProps {
  trigger: ReactNode;
  actions: IMenuAction[];
  side?: RadixSide;
  sideOffset?: number;

  /** header while using dialog mode */
  title: string;

  skipSort?: boolean;

  // for rendering "larger" menus (e.g. for hitter library actions)
  extended?: boolean;

  // keep the menu from being too large
  constrained?: boolean;

  /** optional extra class on the dropdown content (e.g. for viewport-constrained height) */
  contentClassName?: string;

  // if parent needs to trigger something whenever the menu is opened
  onOpenChange?: (opened: boolean) => void;
}

export const CommonMenu = (props: IProps) => {
  const [open, setOpen] = useState(false);

  const actions = useMemo(
    () => setupMenuActions(props.actions, props.skipSort),
    [props.actions, props.skipSort]
  );

  if (actions.length === 0) {
    // to stop typescript from freaking out
    return <>{props.trigger}</>;
  }

  return (
    <ErrorBoundary identifier="CommonMenu">
      <ClickCatcher>
        <DropdownMenu.Root
          open={open}
          onOpenChange={(v) => {
            setOpen(v);

            if (props.onOpenChange) {
              props.onOpenChange(v);
            }
          }}
        >
          <DropdownMenu.Trigger asChild>{props.trigger}</DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className={StringHelper.classNames([
                'MenuContent',
                'scroll-hover',
                props.constrained ? 'Constrained' : undefined,
                props.contentClassName,
              ])}
              side={props.side ?? 'right'}
              sideOffset={props.sideOffset ?? 4}
            >
              {(() => {
                const menuItems = actions.map((a, i) => {
                  if (a.label === SEPARATOR_LABEL) {
                    return (
                      <DropdownMenu.Separator
                        key={i}
                        data-group={a.group}
                        className="MenuSeparator"
                        hidden={i === 0}
                      />
                    );
                  }
                  const color = a.disabled ? RADIX.COLOR.SECONDARY : a.color;
                  return (
                    <DropdownMenu.Item
                      key={i}
                      className={StringHelper.classNames([
                        'MenuItem',
                        props.extended ? 'Extended' : undefined,
                        a.className,
                      ])}
                      data-group={a.group}
                      title={a.tooltip ? t(a.tooltip).toString() : undefined}
                      disabled={a.disabled}
                      onClick={(e) => !a.disabled && a.onClick(e)}
                    >
                      <Flex direction="column" p="1">
                        <Flex gap="1" align="center">
                          {a.prefixIcon && (
                            <Box style={{ marginRight: '8px' }}>
                              <Text color={color}>
                                <Flex align="center">{a.prefixIcon}</Flex>
                              </Text>
                            </Box>
                          )}
                          <Box>
                            <Text
                              color={color}
                              title={t(a.label).toString()}
                              truncate
                            >
                              {t(a.label)}
                            </Text>
                          </Box>
                          {a.suffixIcon && (
                            <Box style={{ marginLeft: '8px' }}>
                              <Text color={color}>
                                <Flex align="center">{a.suffixIcon}</Flex>
                              </Text>
                            </Box>
                          )}
                        </Flex>
                        {a.description && (
                          <Box>
                            <Text
                              color={RADIX.COLOR.NEUTRAL}
                              wrap="balance"
                              style={{ width: '100%' }}
                            >
                              {a.description}
                            </Text>
                          </Box>
                        )}
                      </Flex>
                    </DropdownMenu.Item>
                  );
                });

                if (props.constrained) {
                  // return the items w/n a viewport
                  return <div className="ConstrainedViewport">{menuItems}</div>;
                }

                // just return w/o the viewport
                return menuItems;
              })()}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </ClickCatcher>
    </ErrorBoundary>
  );
};
